import { useState, useEffect } from "react";
import { HomePageIds } from "common/constant";
import { DOC_URLS, openPdfInNewTab } from "utils/openPdfInNewTab";
import { smoothScrollTo } from "utils/smoothScroll";
import Logo from "../../../common/Icons/Logo.svg";
import BlobsImage from "../../../common/Images/Home/blobs.png";

export default function Footer() {
  const [currentYear, setCurrentYear] = useState(2024);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    
    if (sectionElement) {
      const yOffset = -80;
      const targetY = 
        sectionElement.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      
      smoothScrollTo(targetY, 400);
    }
  };

  const openSupportMail = () => {
    window.location.href = "mailto:support@beseenhealth.com";
  };

  const openPressMail = () => {
    window.location.href = "mailto:press@beseenhealth.com";
  };

  // Footer columns for new design
  const footerColumns = [
    {
      title: 'PRODUCT',
      links: [
        { name: 'Why BeSeen?', onClick: () => scrollToSection(HomePageIds.whyBeSeen) },
        { name: 'How It Works', onClick: () => scrollToSection(HomePageIds.howItWorks) },
        { name: 'Pricing', onClick: () => scrollToSection(HomePageIds.pricing) },
        { name: "FAQ's", onClick: () => scrollToSection(HomePageIds.faqs) },
      ],
    },
    {
      title: 'COMPANY',
      links: [
        { name: 'Terms & Conditions', onClick: () => openPdfInNewTab(DOC_URLS.termsAndConditionDoc) },
        { name: 'Privacy Policy', onClick: () => openPdfInNewTab(DOC_URLS.privacyPolicyDoc) },
      ],
    },
    {
      title: 'SUPPORT',
      links: [
        { name: 'Help Center', href: 'https://beseenhealth.crunch.help/en' },
        { name: 'Have a question?', onClick: () => {
          if (typeof window.openHelpCrunch === "function") {
            window.openHelpCrunch();
          }
        }},
        { name: 'Submit Feedback', onClick: () => {
          if (typeof window.openHelpCrunch === "function") {
            window.openHelpCrunch();
          }
        }},
      ],
    },
  ];

  return (
    <div className="bg-[#184353] text-white px-16 md:px-24 py-10 md:py-20 relative overflow-hidden">
      {/* Background blobs image */}
      <div className="absolute bottom-0 right-0 pointer-events-none">
        <img
          src={BlobsImage}
          alt="Footer background decoration"
          width={800}
          height={580}
          className="max-h-[580px] w-auto object-contain opacity-30"
          style={{ maxHeight: '580px' }}
        />
      </div>
      
      {/* Top row: Branding + Links */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start w-full max-w-[1400px] mx-auto gap-10 lg:gap-6 pt-12 pb-24 lg:pt-24 lg:pb-12 relative z-10">
        {/* Branding */}
        <div className="flex flex-col lg:w-1/4 lg:min-w-[220px] mb-24 lg:mb-0 w-full">
          <img
            src={Logo}
            alt="Be:Seen Logo"
            width={200}
            height={88}
            className="cursor-pointer hover:opacity-80 w-[200px] h-[88px]"
          />
          <p className="text-[18px] font-light -mt-2 opacity-50">Simplifying healthcare for patients and providers.</p>
        </div>
        
        {/* Footer Columns */}
        <div className="flex flex-col md:flex-row md:flex-nowrap w-full justify-start self-stretch lg:justify-end items-start gap-16 md:gap-12 xl:gap-32 md:ml-0 max-[584px]:flex-col max-[584px]:items-start">
          {footerColumns.map((col) => (
            <div key={col.title} className="flex flex-col gap-3 lg:gap-7 min-w-[150px]">
              <span className="uppercase font-semibold text-white text-[14px] mb-2 tracking-wider">{col.title}</span>
              {col.links.map((link) =>
                'onClick' in link ? (
                  <button
                    key={link.name}
                    className="font-['Poppins'] text-[14px] font-medium leading-normal opacity-80 text-white hover:opacity-100 cursor-pointer text-left bg-transparent border-0 p-0"
                    onClick={link.onClick}
                  >
                    {link.name}
                  </button>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="font-['Poppins'] text-[14px] font-medium leading-normal opacity-80 text-white hover:opacity-100 cursor-pointer text-left"
                    target={link.href?.startsWith('http') ? '_blank' : undefined}
                    rel={link.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {link.name}
                  </a>
                )
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom row: Copyright */}
      <div className="w-full max-w-[1400px] mx-auto pt-24 pb-12 relative z-10">
        <p className="text-[12px] font-normal opacity-50 text-left">
          Copyright {currentYear} Be Seen Health LLC. All rights reserved.
        </p>
      </div>
    </div>
  );
}